import React, { useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { SettingsSection } from "component/shared/settings-page";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { User } from "model/user";
import { BillingResource } from "api/resource/billing";
import { RootState } from "redux/store";
import { BillingIneligibleReasonType } from "type/billing-ineligible-reason";
import { SUPPORT_EMAIL, SUPPORT_PHONE } from "../support-contact";
import { CancelAccount } from "./cancel-account";
import { CardFormHandle, CardFormState } from "./card-form";
import { Invoices } from "./invoices";
import { PaymentMethod } from "./payment-method";
import { PlanDetails } from "./plan-details";
import { useBillingStyles } from "./styles";

interface Props {
	user: User;
}

const IneligibleMessage: React.FC<{ user: User; summary: BillingResource }> = ({ user, summary }) => {
	const { classes } = useBillingStyles();
	if (BillingIneligibleReasonType.RESELLER_BILLED.matches(summary.reason ?? undefined)) {
		return (
			<SettingsSection title="Billing">
				<Mui.Typography className={classes.value}>
					Your account is billed through your reseller. Contact them to update your payment method,
					change your plan, or get copies of your invoices.
				</Mui.Typography>
			</SettingsSection>
		);
	}
	return (
		<SettingsSection title="Billing">
			<div className={classes.detailList}>
				<Mui.Typography className={classes.value}>
					To update your payment method, change your plan, or get copies of your invoices, contact our
					support team at <Mui.Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Mui.Link> or{" "}
					{SUPPORT_PHONE}.
				</Mui.Typography>
				<Mui.Typography className={classes.value}>
					For faster assistance, please provide your client ID: {user.clientId}
				</Mui.Typography>
			</div>
		</SettingsSection>
	);
};

export const BillingTab: React.FC<Props> = ({ user }) => {
	const { classes } = useBillingStyles();
	const error = useSelector((state: RootState) => state.billing.error);
	const summary = useSelector((state: RootState) => state.billing.summary);
	const formRef = useRef<CardFormHandle>(null);
	const [form, setForm] = useState<CardFormState | null>(null);

	let body: React.ReactNode;
	// A failed refresh after a card update keeps showing the summary already loaded; the load error
	// only replaces the tab when there is nothing to show.
	if (!summary && error) {
		body = (
			<Mui.Alert severity="error" className={classes.alert}>
				We couldn't load your billing details. Please try again later.
			</Mui.Alert>
		);
	} else if (!summary) {
		// The fetch is dispatched by the Account page on mount.
		body = [0, 1, 2].map((index) => (
			<Mui.Skeleton key={index} variant="rectangular" height={160} className={classes.skeleton} />
		));
	} else if (!summary.eligible) {
		body = <IneligibleMessage user={user} summary={summary} />;
	} else {
		body = (
			<>
				<PlanDetails user={user} summary={summary} />
				<PaymentMethod user={user} summary={summary} formRef={formRef} onFormStateChange={setForm} />
				<Invoices user={user} />
			</>
		);
	}

	return (
		<>
			{body}
			{user.reseller.direct && <CancelAccount user={user} />}
			{/* Here rather than in the card form: a sticky bar only sticks within its parent. */}
			{form && (
				<SettingsStickyFooter
					dirty={form.dirty}
					saving={form.saving}
					authError={null}
					refreshed
					onSave={() => formRef.current?.save()}
					onCancel={() => formRef.current?.cancel()}
				/>
			)}
		</>
	);
};
